import { TranslationKey } from './../../../../types/translation/TranslationKey';

import React, { useContext, useMemo, useState } from 'react';
import FlexPane from '../../../../VentoryUI/components/common/FlexPane';
import SearchBarWithFilter from '../../../../VentoryUI/components/common/SearchBarWithFilter';
import { t } from '../../../../types/translation/Translator';
import Table from '../../../components/Table';
import { ProductMasterDataContext } from '../../../../context/ProductMasterDataContext';
import { CompanyContext } from '../../../../context/CompanyContext';
import { UserContext } from '../../../../context/UserContext';
import { Grid } from '@mui/material';
import {
  AssetTransaction,
  AssetTransactionParentType,
  AssetTransactionStatus,
  AssetTransactionType,
} from '../../../../types/asset_transaction/assetTransaction';
import { AssetTransactionContext } from '../../../../context/AssetTransactionContext';
import { AssetTransactionFilter } from '../../../../VentoryUI/components/filters/AssetTransactionFilter/AssetTransactionFilter';
import { AssetTransactionTable } from '../../Common/AssetTransactionTable';
import {
  AssetTransactionColumn,
  AssetTransactionTableSettings,
} from '../../../../types/asset_transaction/assetTransaction.table';
import SignAssetModal from '../Modals/SignAssetModal';
import { AssetContext } from '../../../../context/AssetContext';
import { CustomFieldContext } from '../../../../context/CustomFieldContext';
import { TagRelationContext } from '../../../../context/TagRelationContext';
import { TableSettingsContext } from '../../../../context/TableSettingsContext';
import { EntityType } from '../../../../types/comment';

export default function UserAssetPendingPane() {
  const { currentCompany } = useContext(CompanyContext);
  const { productMasterData, productMasterDataLoading } = useContext(ProductMasterDataContext);
  const { assetTransactions, assetTransactionsLoading } = useContext(AssetTransactionContext);
  const { assets } = useContext(AssetContext);
  const { getTableSettings } = useContext(TableSettingsContext);
  const { companyUsers, currentUser, locale } = useContext(UserContext);
  const { customFields } = useContext(CustomFieldContext);
  const { tagRelations } = useContext(TagRelationContext);

  const assetTableSettings = getTableSettings(EntityType.asset, currentCompany.id);

  const [filteredItems, setFilteredItems] = useState<AssetTransaction[]>([]);
  const [selected, setSelected] = useState<AssetTransaction | undefined>();

  const [openSignModal, setOpenSignModal] = useState<boolean>(false);

  const items = useMemo(() => {
    const hasOpen = new Set(
      [...assetTransactions.values()]
        .filter(
          transaction =>
            transaction.parentType === AssetTransactionParentType.reassign &&
            transaction.type === AssetTransactionType.outbound &&
            transaction.status === AssetTransactionStatus.pending,
        )
        .map(transaction => transaction.parentId),
    );

    const values = [...assetTransactions.values()].filter(
      transaction =>
        currentUser?.id &&
        transaction.location?.entityId === currentUser.id &&
        ((transaction.status === AssetTransactionStatus.pending &&
          (transaction.type === AssetTransactionType.outbound || !hasOpen.has(transaction.parentId))) ||
          (transaction.type === AssetTransactionType.verify && transaction.status === AssetTransactionStatus.created)),
    );
    setFilteredItems(values);
    return values;
  }, [assetTransactions]);

  const columns = [
    {
      index: 0,
      column: AssetTransactionColumn.productName,
      label: t(TranslationKey.assetName).singular.label,
    },
    {
      index: 1,
      column: AssetTransactionColumn.productNumber,
      label: t(TranslationKey.assetNumber).singular.label,
    },
    {
      index: 2,
      column: AssetTransactionColumn.createdAt,
      label: t(TranslationKey.createdAt).singular.label,
    },
    {
      index: 3,
      column: AssetTransactionColumn.sign,
      label: '',
    },
    {
      index: 4,
      column: AssetTransactionColumn.pendingQuantity,
      label: t(TranslationKey.quantity).singular.label,
    },
  ];

  const filter = new AssetTransactionFilter(
    productMasterData,
    companyUsers,
    assets,
    assetTransactions,
    assetTableSettings,
    customFields,
    tagRelations,
    currentCompany,
  );

  return (
    <>
      <SignAssetModal open={openSignModal} setOpen={setOpenSignModal} transaction={selected} />

      <FlexPane
        header={
          <SearchBarWithFilter
            loading={assetTransactionsLoading || productMasterDataLoading}
            items={items}
            setItems={setFilteredItems}
            placeholder={t(TranslationKey.searchForAsset).plural.label}
            filter={filter}
          />
        }
        content={
          <Grid container rowSpacing={1} alignContent={'start'} className='h-full'>
            <Grid item xs={12} className='h-full'>
              <Table
                loading={assetTransactionsLoading || productMasterDataLoading}
                items={filteredItems}
                headers={AssetTransactionTable.header(
                  productMasterData,
                  companyUsers,
                  currentCompany,
                  new AssetTransactionTableSettings({ companyId: currentCompany.id, columns: columns }),
                  locale,
                )}
                onClick={item => {
                  setSelected(item);
                  setOpenSignModal(true);
                }}
                totalItemCount={items.length}
                filter={filter}
              />
            </Grid>
          </Grid>
        }
      />
    </>
  );
}
